import React from 'react';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          maxWidth: '840px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
          border: '1px solid #E2E8F0',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 28px',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#FAF8F5',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#2D2826' }}>
              {title}
            </h2>
            <span style={{ fontSize: '12px', color: '#64748B' }}>
              Luminous Break Inc. • Enterprise Legal & Regulatory Compliance
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close modal"
            style={{
              background: '#FFFFFF',
              border: '1px solid #CBD5E1',
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '14px',
              cursor: 'pointer',
              color: '#475569',
              fontWeight: 'bold',
            }}
          >
            ✕ Close
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{
          padding: '28px',
          overflowY: 'auto',
          fontSize: '13.5px',
          lineHeight: '1.7',
          color: '#334155',
        }}>
          {children}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 28px',
          borderTop: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#FAF8F5',
          borderBottomLeftRadius: '20px',
          borderBottomRightRadius: '20px',
        }}>
          <span style={{ fontSize: '11.5px', color: '#64748B' }}>
            Legal Inquiries: <a href="mailto:legal@luminousbreak.com" style={{ color: '#2D5A27' }}>legal@luminousbreak.com</a>
          </span>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#2D5A27',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 24px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            I Understand & Agree
          </button>
        </div>
      </div>
    </div>
  );
};

export const PrivacyPolicyContent: React.FC = () => (
  <div>
    <p style={{ color: '#64748B', fontSize: '12px', marginTop: 0 }}>
      <strong>Last Updated & Effective Date:</strong> August 30, 2026 | Version 2.2
    </p>

    <p>
      Luminous Break Inc. ("Luminous Break", "we", "us", or "our") is dedicated to upholding the highest standards of data privacy, confidentiality, and security. This Privacy Policy describes how we handle information across our mobile applications, desktop browser extensions, web applications, and clinician portals (collectively, the "Services").
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      1. Core Philosophy: On-Device Processing & Zero-Knowledge Architecture
    </h3>
    <p>
      Unlike conventional monitoring software, Luminous Break is engineered with strict <strong>Data Minimization</strong> and <strong>Privacy by Design</strong> principles:
    </p>
    <ul>
      <li><strong>Local Evaluation:</strong> All trigger detection, website domain matching, and behavioral pause activations occur strictly on your personal device hardware.</li>
      <li><strong>No Browsing History Egress:</strong> Your browsing URLs, keystrokes, viewing histories, and personal journal reflections are <strong>never</strong> transmitted to, stored on, or accessible by Luminous Break servers.</li>
      <li><strong>Encrypted Hardware Keystores:</strong> Your personal configuration preferences are sealed on-device using hardware-backed enclaves (Apple Keychain Services on iOS/macOS and Android KeyStore).</li>
    </ul>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      2. Categories of Information Processed
    </h3>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', marginBottom: '16px' }}>
      <thead>
        <tr style={{ backgroundColor: '#F1F5F9', textAlign: 'left' }}>
          <th style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Category</th>
          <th style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Specific Data Elements</th>
          <th style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Storage Location & Retention</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1', fontWeight: 600 }}>Local Behavioral Data</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Target URLs, app package names, CBT journaling inputs, diaphragmatic breathing logs.</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>100% on-device local storage. Deleted immediately upon app uninstall or reset.</td>
        </tr>
        <tr>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1', fontWeight: 600 }}>Shared Care Team Metrics (Optional)</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Aggregate pause counts, urge drop % rating, habit streak days (never specific URLs).</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>End-to-end encrypted; decrypted strictly in your authorized clinician's browser RAM.</td>
        </tr>
        <tr>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1', fontWeight: 600 }}>Firebase Authentication (Optional Paired Mode)</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Pseudonymous user UID generated strictly upon optional clinician pairing or practitioner sign-in.</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Firebase Auth cloud service. Purged immediately upon in-app account deletion.</td>
        </tr>
        <tr>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1', fontWeight: 600 }}>Cloud Firestore (Pairing & Recipes)</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>6-digit ephemeral pairing PINs, encrypted patient intervention recipes, and care circle associations.</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Cloud Firestore (cognitiveintercept). Ephemeral PINs expire in 15 minutes; recipes deleted upon unpair or vault shred.</td>
        </tr>
        <tr>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1', fontWeight: 600 }}>Firebase Cloud Storage (Voice Memos)</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Clinician-recorded audio voice memos (.m4a/.mp3) delivered during patient pause interventions.</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Firebase Storage (cognitiveintercept.firebasestorage.app). Accessible strictly by paired patient and authoring clinician.</td>
        </tr>
        <tr>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1', fontWeight: 600 }}>Account & Billing Data</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Clinician email, clinic domain, subscription receipt tokens (Stripe / Apple StoreKit / Google Play).</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Encrypted cloud database. Retained for active subscription duration + statutory tax period.</td>
        </tr>
        <tr>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1', fontWeight: 600 }}>Account Deletion & Vault Shredding</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>Zero-knowledge keystore keys, local database tokens, and cloud pairing records.</td>
          <td style={{ padding: '8px', border: '1px solid #CBD5E1' }}>1-tap irreversible cryptographic shredding in Settings (Settings → Membership & Care Circle → Delete Account & Shred Vault).</td>
        </tr>
      </tbody>
    </table>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      3. Strict Prohibition on Data Selling & Advertising
    </h3>
    <p>
      Luminous Break maintains a strict zero-monetization policy regarding personal information:
    </p>
    <ul>
      <li>We do <strong>not</strong> sell, rent, license, or disclose your personal data to third parties, data brokers, or advertising networks under any circumstances (satisfying CCPA/CPRA § 1798.120).</li>
      <li>We do <strong>not</strong> perform cross-context behavioral tracking or interest-based profiling.</li>
      <li>Our products are 100% ad-free and funded exclusively through transparent software subscriptions.</li>
    </ul>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      4. HIPAA & Protected Health Information (PHI) Alignment
    </h3>
    <p>
      Where Luminous Break is licensed by healthcare providers, clinics, or covered entities subject to the Health Insurance Portability and Accountability Act (HIPAA), we act strictly as a <strong>Business Associate</strong> pursuant to a signed Business Associate Agreement (BAA). We maintain administrative, physical, and technical safeguards complying with 45 C.F.R. Part 164, Subpart C (HIPAA Security Rule).
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      5. Your Rights: Access, Export, and Deletion (GDPR & CCPA)
    </h3>
    <p>
      Regardless of your geographic jurisdiction, Luminous Break provides you with complete control over your data:
    </p>
    <ul>
      <li><strong>Right to Deletion / Erasure:</strong> You can purge all local metrics at any time inside the app settings. Deleting the app immediately destroys all on-device records.</li>
      <li><strong>Right to Revoke Care Team Sharing:</strong> You can disconnect any paired therapist, coach, or multidisciplinary care team member with a single tap.</li>
      <li><strong>Data Portability:</strong> Clinicians and account owners may request an export of their administrative account records by emailing <a href="mailto:privacy@luminousbreak.com" style={{ color: '#2D5A27' }}>privacy@luminousbreak.com</a>.</li>
    </ul>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      6. Children's Privacy
    </h3>
    <p>
      Our Services are not directed to children under 13 years of age (or under 16 in the European Economic Area). If you are between 13 and 17, you may use the Services only with the verified consent of a parent, legal guardian, or treating healthcare provider.
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      7. Contacting Our Data Protection Officer
    </h3>
    <p>
      For questions, data subject requests, or regulatory inquiries, contact:
      <br />
      <strong>Luminous Break Inc. — Privacy & Legal Office</strong>
      <br />
      Email: <a href="mailto:privacy@luminousbreak.com" style={{ color: '#2D5A27' }}>privacy@luminousbreak.com</a>
    </p>
  </div>
);

export const TermsOfServiceContent: React.FC = () => (
  <div>
    <p style={{ color: '#64748B', fontSize: '12px', marginTop: 0 }}>
      <strong>Last Updated & Effective Date:</strong> August 30, 2026 | Version 3.0 (Enterprise Comprehensive)
    </p>

    <p>
      These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and Luminous Break Inc. ("Luminous Break", "we", "us", or "our"). These Terms govern your access to and use of our mobile applications (iOS, Android), desktop applications (macOS, Windows), browser extensions (Chrome, Safari, Edge, Firefox), websites, and clinical portals (collectively, the "Services").
    </p>

    {/* Section 0: Emergency Health Box */}
    <div style={{
      backgroundColor: '#FEF3C7',
      borderRadius: '12px',
      padding: '18px 20px',
      border: '1.5px solid #F59E0B',
      color: '#92400E',
      marginBottom: '24px',
    }}>
      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        ⚠️ CRITICAL MEDICAL & EMERGENCY CRISIS DISCLAIMER — NOT MEDICAL CARE
      </h4>
      <p style={{ margin: 0, fontSize: '12.5px', lineHeight: '1.6' }}>
        <strong>Luminous Break is a digital cognitive behavioral habit and mindfulness software tool. The Services DO NOT provide medical advice, diagnosis, treatment, clinical psychotherapy, psychiatric care, or emergency suicide prevention services.</strong>
        <br /><br />
        If you are experiencing a mental health emergency, thoughts of suicide, severe substance withdrawal, physical crisis, or danger to yourself or others, <strong>DO NOT rely on this application</strong>. Immediately contact:
        <br />
        • <strong>United States & Canada:</strong> Call or text <strong>988</strong> (Suicide & Crisis Lifeline) or call <strong>911</strong>.
        <br />
        • <strong>United Kingdom:</strong> Call <strong>111</strong> or <strong>999</strong>.
        <br />
        • <strong>European Union:</strong> Call <strong>112</strong>.
        <br />
        • <strong>Crisis Text Line:</strong> Text <strong>HOME</strong> to <strong>741741</strong>.
      </p>
    </div>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      1. Acceptance of Terms & Electronic Contracting
    </h3>
    <p>
      By downloading, creating an account, pairing with a clinician, or using any part of the Services, you affirm that you are at least 18 years old (or possess verified legal parental/guardian consent if between 13 and 17) and possess legal capacity to enter into these Terms. You consent to conduct transactions and receive legal notices electronically pursuant to the Electronic Signatures in Global and National Commerce Act (E-SIGN Act, 15 U.S.C. § 7001).
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      2. Scope of Services & User Device Governance
    </h3>
    <p>
      Luminous Break provides on-device software utilities that introduce intentional pauses, diaphragmatic breathing pacers, cognitive reframing prompts, and custom shield delays upon detecting user-selected application or website destinations. You acknowledge and agree that:
    </p>
    <ul>
      <li>You retain ultimate operational control over your device settings, permissions (including Screen Time, Accessibility Services, and Extension permissions), and guardrail rules.</li>
      <li>You are solely responsible for configuring your protection categories, work-hours whitelists, and emergency bypass settings.</li>
      <li>The software is an adjunct personal wellness tool and does not guarantee specific psychological, financial, or behavioral outcomes.</li>
    </ul>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      3. Subscription Tiers, Free Trials, and Automatic Renewals
    </h3>
    <p>
      In compliance with the California Automatic Renewal Law (Cal. Bus. & Prof. Code § 17600 et seq.), New York ARL, and FTC Negative Option Regulations (16 C.F.R. Part 425):
    </p>
    <ul>
      <li><strong>Free Core Starter ($0):</strong> Provides permanent access to essential screen-time and media guardrails with zero credit card requirement.</li>
      <li><strong>14-Day Free Trials:</strong> Paid individual subscriptions ("Mindful Plus") may begin with a 14-day free trial. If you do not cancel before the trial period concludes, your designated payment method will be billed at the standard recurring rate.</li>
      <li><strong>Automatic Continuous Renewal:</strong> Subscriptions automatically renew at the conclusion of each billing period (monthly or annually) at the then-current subscription price unless affirmatively cancelled prior to renewal.</li>
      <li><strong>Notice of Price Changes:</strong> We will provide at least thirty (30) days advance notice via email or prominent in-app notification prior to any rate adjustments.</li>
    </ul>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      4. Comprehensive Cancellation & Refund Policy
    </h3>
    <p>
      We maintain a zero-dark-patterns, fully transparent cancellation standard across all platforms:
    </p>
    <div style={{ backgroundColor: '#F8FAFC', padding: '16px', borderRadius: '10px', border: '1px solid #CBD5E1', marginBottom: '14px' }}>
      <h5 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#1E293B', fontWeight: 700 }}>
        How to Cancel by Platform:
      </h5>
      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12.5px', color: '#334155' }}>
        <li><strong>Apple iOS / App Store Purchases:</strong> Open your iPhone/iPad <em>Settings ➔ [Your Name] ➔ Subscriptions ➔ Luminous Break ➔ Cancel Subscription</em>. You must cancel at least 24 hours before the renewal date.</li>
        <li><strong>Google Play Store Purchases:</strong> Open the Google Play app <em>➔ Profile Icon ➔ Payments & Subscriptions ➔ Subscriptions ➔ Luminous Break ➔ Cancel Subscription</em>.</li>
        <li><strong>Web Portal / Direct Clinic Subscriptions:</strong> Clinic Administrators can cancel directly inside the Coaching Portal under <em>Organization & Team ➔ Billing ➔ Cancel Subscription</em> with 1-click self-service.</li>
      </ul>
    </div>
    <div style={{ backgroundColor: '#F8FAFC', padding: '16px', borderRadius: '10px', border: '1px solid #CBD5E1' }}>
      <h5 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#1E293B', fontWeight: 700 }}>
        Refund Standards & Money-Back Guarantees:
      </h5>
      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12.5px', color: '#334155' }}>
        <li><strong>Direct Web & Clinic 30-Day Money-Back Guarantee:</strong> For direct web subscriptions and annual clinic plans, you are entitled to a full 100% refund if requested within <strong>thirty (30) calendar days</strong> of initial purchase or renewal by contacting <a href="mailto:billing@luminousbreak.com" style={{ color: '#2D5A27' }}>billing@luminousbreak.com</a>.</li>
        <li><strong>In-App Purchase Refunds:</strong> Apple and Google maintain exclusive refund authority for in-app purchases processed through their stores pursuant to Apple Media Services Terms and Google Play Terms.</li>
        <li><strong>Retained Access:</strong> Upon cancellation, your account retains full access to all unlocked features through the conclusion of your current prepaid billing cycle.</li>
      </ul>
    </div>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      5. Healthcare Provider Terms & Multidisciplinary Care Teams
    </h3>
    <p>
      If you access the Services as a licensed therapist, physician, clinic director, or healthcare organization ("Practitioner"):
    </p>
    <ul>
      <li><strong>Independent Clinical Judgment:</strong> You maintain sole responsibility for all clinical decisions, medical advice, patient diagnosis, psychotherapy, and risk assessments. Luminous Break is strictly an administrative and behavioral adjunct tool.</li>
      <li><strong>Business Associate Agreement (BAA):</strong> For Covered Entities under HIPAA, your organization must execute our standardized BAA pursuant to 45 C.F.R. § 164.502(e) and § 164.504(e).</li>
      <li><strong>Multidisciplinary Care Team Governance:</strong> Lead Clinicians are responsible for ensuring that invited specialists (dietitians, psychiatric staff) possess appropriate authorization and patient consent for granted behavioral domain scopes.</li>
    </ul>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      6. Intellectual Property & License Restrictions
    </h3>
    <p>
      The Services, including all proprietary software algorithms, on-device heuristic inference engines, visual breathing mandalas, audio recordings, design tokens, and trademarks, are the exclusive intellectual property of Luminous Break Inc. We grant you a limited, personal, revocable, non-exclusive, non-sublicensable, and non-transferable license to use the software.
      <br /><br />
      You agree NOT to: (a) reverse engineer, decompile, disassemble, or derive source code from any application binary; (b) circumvent or tamper with cryptographic key wrapping, hardware enclave isolation, or access control lists; (c) white-label, resell, or distribute the Services without written enterprise authorization; or (d) use automated bots or scrapers to extract platform data.
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      7. User-Authored Content & Collaborative Voice Memos
    </h3>
    <p>
      You retain all intellectual property rights in your personal reflection journal entries and clinician-recorded audio/video memos. By uploading custom prompts or recordings, you grant Luminous Break a limited license solely to process and deliver such content to your designated authorized devices and care team members in accordance with your Data Access Control Lists (ACLs).
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      8. Disclaimer of Warranties
    </h3>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW:
      <br />
      (a) THE SERVICES ARE PROVIDED STRICTLY ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, AND UNINTERRUPTED AVAILABILITY.
      <br />
      (b) LUMINOUS BREAK DOES NOT WARRANT THAT THE SERVICES WILL BE ERROR-FREE, SECURE FROM ALL THREATS, OR THAT ON-DEVICE DETECTION WILL CAPTURE 100% OF TARGETED IMPULSIVE VISITS ACROSS THIRD-PARTY PLATFORM UPDATES.
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      9. Limitation of Liability
    </h3>
    <p>
      TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW:
      <br />
      (a) IN NO EVENT SHALL LUMINOUS BREAK INC., ITS DIRECTORS, OFFICERS, EMPLOYEES, AFFILIATES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING DAMAGES FOR LOSS OF PROFITS, LOSS OF REVENUE, DATA LOSS, BUSINESS INTERRUPTION, PERSONAL DISTRESS, MEDICAL EXPENSES, OR EMOTIONAL HARM, REGARDLESS OF THE CAUSE OF ACTION (WHETHER IN CONTRACT, TORT, STRICT LIABILITY, OR NEGLIGENCE).
      <br />
      (b) OUR TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE GREATER OF: (I) ONE HUNDRED DOLLARS ($100.00 USD), OR (II) THE TOTAL FEES ACTUALLY PAID BY YOU TO LUMINOUS BREAK IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO LIABILITY.
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      10. Indemnification
    </h3>
    <p>
      You agree to defend, indemnify, and hold harmless Luminous Break Inc., its officers, directors, employees, and agents from and against any third-party claims, liabilities, damages, judgments, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or relating to: (a) your violation of these Terms; (b) your violation of applicable law or third-party rights; (c) your user-authored content; or (d) in the case of Practitioners, your provision of professional healthcare services or clinical negligence.
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      11. Mandatory Binding Individual Arbitration & Class Action Waiver
    </h3>
    <p>
      <strong>PLEASE READ THIS SECTION CAREFULLY. IT MATERIALLY AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.</strong>
    </p>
    <ul>
      <li><strong>Binding Arbitration:</strong> Any dispute, claim, or controversy arising out of or relating to these Terms or the Services shall be settled exclusively by final, binding individual arbitration administered by JAMS in accordance with the JAMS Comprehensive Arbitration Rules.</li>
      <li><strong>Class Action & Jury Waiver:</strong> YOU AND LUMINOUS BREAK AGREE THAT ALL CLAIMS MUST BE BROUGHT IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. YOU SPECIFICALLY WAIVE ANY RIGHT TO A TRIAL BY JURY.</li>
      <li><strong>30-Day Arbitration Opt-Out:</strong> You have the right to opt out of this arbitration provision within thirty (30) calendar days of initial account creation by sending written notice to <a href="mailto:arbitration-opt-out@luminousbreak.com" style={{ color: '#2D5A27' }}>arbitration-opt-out@luminousbreak.com</a> stating your full legal name, associated account email, and unequivocal intent to opt out.</li>
    </ul>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      12. Governing Law, Jurisdiction & Severability
    </h3>
    <p>
      These Terms and any dispute related thereto shall be governed by and construed in accordance with the laws of the State of California and the United States Federal Arbitration Act (FAA), without regard to conflict of laws principles. If any provision of these Terms is determined to be unlawful, void, or unenforceable, that provision shall be deemed severable and shall not affect the validity and enforceability of remaining provisions.
    </p>

    <h3 style={{ color: '#2D2826', fontSize: '16px', marginTop: '24px', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px' }}>
      13. Official Corporate & Legal Notices
    </h3>
    <p>
      For formal legal notices, billing inquiries, or compliance documentation:
      <br />
      <strong>Luminous Break Inc. — Legal & Corporate Affairs</strong>
      <br />
      500 Howard Street, Suite 400, San Francisco, CA 94105, United States
      <br />
      Legal Office: <a href="mailto:legal@luminousbreak.com" style={{ color: '#2D5A27' }}>legal@luminousbreak.com</a>
      <br />
      Billing Inquiries: <a href="mailto:billing@luminousbreak.com" style={{ color: '#2D5A27' }}>billing@luminousbreak.com</a>
      <br />
      Privacy Officer: <a href="mailto:privacy@luminousbreak.com" style={{ color: '#2D5A27' }}>privacy@luminousbreak.com</a>
    </p>
  </div>
);
